// Authentication Service
// Talks to the server-side /api/auth gatekeeper. The database key and all
// password hashing now live on the server — they are NEVER shipped to the
// browser. This file only handles the request + local auto-login storage.

import { DEFAULT_AVATAR } from '../config/avatarConfig';

const API_URL = '/api/auth';

// LocalStorage keys
const AUTH_STORAGE_KEY = 'onepiece_quiz_auth';

// POST a JSON action to the auth API
const callAuth = async (payload) => {
  const response = await fetch(API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

// Check if username exists
export const checkUsernameExists = async (username) => {
  const data = await callAuth({ action: 'check', username });
  return !!data.exists;
};

// Register a new user
export const registerUser = async (username, password, avatar = DEFAULT_AVATAR, email = '') => {
  try {
    const result = await callAuth({ action: 'register', username, password, avatar, email });
    if (!result.success) {
      return { success: false, error: result.error || 'Failed to register' };
    }
    // Save to localStorage for auto-login (include avatar)
    saveAuthToLocalStorage(username, password, avatar);
    return { success: true, username, avatar };
  } catch (error) {
    console.error('Error registering user:', error);
    return { success: false, error: error.message };
  }
};

// Login user
export const loginUser = async (username, password) => {
  try {
    const result = await callAuth({ action: 'login', username, password });
    if (!result.success) {
      // The server looked and said no. Only this is a real "wrong password";
      // a failed request below is not.
      // Only "Incorrect password" counts: a missing name could be the server
      // reading the wrong place, and must never sign anyone out.
      return { success: false, rejected: result.error === 'Incorrect password', error: result.error || 'Login failed' };
    }
    const avatar = result.avatar || DEFAULT_AVATAR;
    saveAuthToLocalStorage(result.username, password, avatar);
    return { success: true, username: result.username, avatar };
  } catch (error) {
    console.error('Error logging in:', error);
    return { success: false, error: error.message };
  }
};

// Save auth to localStorage
const saveAuthToLocalStorage = (username, password, avatar = DEFAULT_AVATAR) => {
  const authData = {
    username,
    // Stored for auto-login (Base64 obfuscated, not security-critical)
    password: btoa(password),
    avatar: avatar || DEFAULT_AVATAR,
  };
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authData));
};

// Get saved auth from localStorage
export const getSavedAuth = () => {
  try {
    const authData = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!authData) return null;

    const parsed = JSON.parse(authData);
    return {
      username: parsed.username,
      password: atob(parsed.password),
      avatar: parsed.avatar || DEFAULT_AVATAR,
    };
  } catch (error) {
    console.error('Error reading auth from localStorage:', error);
    return null;
  }
};

// Clear auth from localStorage (logout)
// Signing out never destroys a password. Most players never saw theirs (quick
// save made it), so a deleted one is an account lost for good. The last ones
// used on this device are kept aside, and the login page offers them back.
const BACKUP_KEY = 'onepiece_quiz_auth_backup';

export const getSavedLogins = () => {
  try { return JSON.parse(localStorage.getItem(BACKUP_KEY)) || []; } catch (e) { return []; }
};

export const clearAuth = () => {
  try {
    const current = localStorage.getItem(AUTH_STORAGE_KEY);
    if (current) {
      const parsed = JSON.parse(current);
      const others = getSavedLogins().filter((b) => String(b.username).toLowerCase() !== String(parsed.username).toLowerCase());
      localStorage.setItem(BACKUP_KEY, JSON.stringify([parsed, ...others].slice(0, 5)));
    }
  } catch (e) { /* keep going: signing out must still work */ }
  localStorage.removeItem(AUTH_STORAGE_KEY);
};


// Verify saved auth is still valid (for auto-login)
export const verifySavedAuth = async () => {
  const savedAuth = getSavedAuth();
  if (!savedAuth) return { valid: false };

  const result = await loginUser(savedAuth.username, savedAuth.password);
  return {
    valid: result.success,
    rejected: !!result.rejected,
    username: result.success ? result.username : null,
    avatar: result.success ? result.avatar : null,
  };
};

// Update user avatar (uses the saved password so only the logged-in owner can change it)
export const updateUserAvatar = async (username, newAvatar) => {
  try {
    const savedAuth = getSavedAuth();
    const password = savedAuth ? savedAuth.password : null;
    if (!password) {
      return { success: false, error: 'Not logged in' };
    }
    const result = await callAuth({ action: 'updateAvatar', username, password, newAvatar });
    if (!result.success) {
      return { success: false, error: result.error || 'Failed to save avatar' };
    }
    // Update localStorage
    saveAuthToLocalStorage(savedAuth.username, password, newAvatar);
    return { success: true, avatar: newAvatar };
  } catch (error) {
    console.error('Error updating avatar:', error);
    return { success: false, error: error.message };
  }
};

// Get user avatar (for looking up other users' avatars)
export const getUserAvatar = async (username) => {
  try {
    const data = await callAuth({ action: 'getAvatar', username });
    return data.avatar || DEFAULT_AVATAR;
  } catch (error) {
    console.error('Error getting user avatar:', error);
    return DEFAULT_AVATAR;
  }
};

// Password validation
export const validatePassword = (password) => {
  if (!password || password.length < 6) {
    return { valid: false, error: 'Password must be at least 6 characters' };
  }
  return { valid: true };
};

// The owner's own email. Both need the saved password, so nobody else can read
// or change it.
export const getMyEmail = async () => {
  const auth = getSavedAuth();
  if (!auth) return { success: false };
  try {
    return await callAuth({ action: 'getEmail', username: auth.username, password: auth.password });
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const setMyEmail = async (email) => {
  const auth = getSavedAuth();
  if (!auth) return { success: false, error: 'Not signed in' };
  try {
    return await callAuth({ action: 'setEmail', username: auth.username, password: auth.password, email });
  } catch (error) {
    return { success: false, error: 'Could not save. Check your connection and try again.' };
  }
};

// Files a "forgot password" request. Nothing about the account changes.
export const requestPasswordHelp = async (username, contact, message = '') => {
  try {
    return await callAuth({ action: 'forgot', username, contact, message });
  } catch (error) {
    return { success: false, error: 'Could not send. Check your connection and try again.' };
  }
};

// The signed-in player's rooms and challenges, for their profile only.
export const getMyRooms = async () => {
  const auth = getSavedAuth();
  if (!auth) return { success: false };
  try {
    const response = await fetch('/api/room', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'mine', username: auth.username, password: auth.password }),
    });
    return await response.json();
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Same account, new name: scores, rooms and races move with it (server side).
export const renameAccount = async (newUsername) => {
  const auth = getSavedAuth();
  if (!auth) return { success: false, error: 'Not signed in' };
  try {
    const result = await callAuth({ action: 'rename', username: auth.username, password: auth.password, newUsername });
    if (result.success) saveAuthToLocalStorage(result.username, auth.password, result.avatar);
    return result;
  } catch (error) {
    return { success: false, error: 'Could not reach the server. Try again.' };
  }
};
